import React, { Component } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { Link } from "react-router-dom";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Appointment from "../components/Appointment.tsx";
import LeaveModal from "../components/LeaveModal.tsx";
import UserService from "../services/user.service";
import TechnicianService from "../services/technician.service";
import type {
  AppointmentItem,
  AppointmentStatus,
  CalendarDisplaySettings,
  LeaveItem,
} from "./models";

// const statuses = {
//   draft: 'Open',
//   added: 'Added',
//   confirmed: 'Confirmed',
//   paid: 'Paid',
//   done: 'Done',
//   client_not_show_up: 'Client do not show up',
// };

const localizer = momentLocalizer(moment);
const BigCalendar: React.ElementType = Calendar as unknown as React.ElementType;

const eventStyleGetter = (event: any) => {
  if (event.isLeave) {
    return {
      style: {
        backgroundColor: "#7209b7",
        color: "white",
      },
    };
  }
  const backgroundColorGetter = (status: AppointmentStatus) => {
    switch (status) {
      case "draft":
        return "#2D9BF0";
      case "day_off":
        return "#ffc107";
      case "added":
        return "yellow";
      case "confirmed":
        return "red";
      case "paid":
        return "#87C54D";
      case "done":
        return "#10A689";
      case "client_not_show_up":
        return "black";
      default:
        return "white";
    }
  };

  const textColor = (status: AppointmentStatus) => {
    switch (status) {
      case "added":
        return "black";
      case "day_off":
        return "black";
      default:
        return "white";
    }
  };

  var style = {
    backgroundColor: backgroundColorGetter(event.status),
    color: textColor(event.status),
  };
  return {
    style: style,
  };
};

type HomeState = {
  content: string;
  appointmentsData: AppointmentItem[];
  leavesData: LeaveItem[];
  showAppointment: boolean;
  currentAppointment: AppointmentItem | null;
  showLeaveModal: boolean;
  selectedLeave: LeaveItem | null;
  showLeaves: boolean;
  date: Date;
} & CalendarDisplaySettings;

const buildCalendarTime = (hour: number, isEnd = false) => {
  return new Date(1972, 0, 1, hour, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
};

export default class Home extends Component<Record<string, never>, HomeState> {
  interval?: ReturnType<typeof setInterval>;

  constructor(props: Record<string, never>) {
    super(props);

    this.state = {
      content: "",
      appointmentsData: [],
      leavesData: [],
      showAppointment: false,
      currentAppointment: null,
      showLeaveModal: false,
      selectedLeave: null,
      showLeaves: localStorage.getItem("showLeaves") !== "false",
      date: new Date(),
      calendarStartHour: 8,
      calendarEndHour: 18,
    };
  }

  hideModal = () => {
    this.setState({ showAppointment: false });
  };


  handleSelect = (props: AppointmentItem) => {
    // prepare new appointment
    const draftAppointment = { ...props, status: "draft" };
    this.setState({ showAppointment: true, currentAppointment: draftAppointment });
  };

  onAppointmentChanged = () => {
    this.getAppointmentsAndLeaves();
  };

  handleShowLeavesChangedFromEvent = () => {
    this.setState({
      showLeaves: localStorage.getItem("showLeaves") !== "false"
    }, () => {
      this.getAppointmentsAndLeaves();
    });
  };

  handleLeavesUpdatedFromEvent = () => {
    this.getAppointmentsAndLeaves();
  };

  componentDidMount() {
    this.loadCalendarSettings();
    this.getAppointmentsAndLeaves();
    window.addEventListener("showLeavesChanged", this.handleShowLeavesChangedFromEvent);
    window.addEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
    this.interval = setInterval(() => {
      this.getAppointmentsAndLeaves();
    }, 15000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener("showLeavesChanged", this.handleShowLeavesChangedFromEvent);
    window.removeEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
  }

  loadCalendarSettings = async () => {
    try {
      const result = await UserService.getAccountSettings();
      this.setState({
        calendarStartHour: result.data.data?.calendarStartHour ?? 8,
        calendarEndHour: result.data.data?.calendarEndHour ?? 18,
      });
    } catch (error) {
      console.log(error);
    }
  };

  getAppointmentsAndLeaves = async (props?: Date) => {
    try {
      const date = props ? props : this.state.date;
      const firstDay = moment(
        new Date(date.getFullYear(), date.getMonth(), -10)
      )
        .toISOString()
        .replace("Z", "");
      const lastDay = moment(
        new Date(date.getFullYear(), date.getMonth() + 1, 10)
      )
        .toISOString()
        .replace("Z", "");

      const appointmentsPromise = UserService.getAppointments(firstDay, lastDay);
      const leavesPromise = this.state.showLeaves
        ? TechnicianService.getLeaves(firstDay, lastDay)
        : Promise.resolve({ data: { data: [] } });

      const [appointmentsResult, leavesResult] = await Promise.all([
        appointmentsPromise,
        leavesPromise,
      ]);

      const newAppointments = appointmentsResult.data.data || [];
      const newLeaves = (leavesResult.data.data || []).map((leave: any) => ({
        ...leave,
        isLeave: true,
      }));

      if (
        JSON.stringify(this.state.appointmentsData) !== JSON.stringify(newAppointments) ||
        JSON.stringify(this.state.leavesData) !== JSON.stringify(newLeaves)
      ) {
        this.setState({ appointmentsData: newAppointments, leavesData: newLeaves });
      }
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error
      ) {
        const apiError = error as { response?: { data?: unknown } };
        console.log(error);
        console.log(apiError.response?.data);
      }
    }
  };

  handleShowLeavesChange = (showLeaves: boolean) => {
    localStorage.setItem("showLeaves", String(showLeaves));
    this.setState({ showLeaves }, () => {
      this.getAppointmentsAndLeaves();
    });
  };

  showModal = (props: any) => {
    if (props.isLeave) {
      this.setState({ showLeaveModal: true, selectedLeave: props });
    } else {
      this.setState({ showAppointment: true, currentAppointment: props });
    }
  };

  formatEndDate = (props: any) => {
    return new Date(props.endDateTime || props.end_date_time);
  };

  formatStartDate = (props: any) => {
    return new Date(props.startDateTime || props.start_date_time);
  };

  formatTitle = (event: any) => {
    if (event.isLeave) {
      return `Vacation: ${event.technicianName || event.technician_name || "Technician"}`;
    }
    const hour = moment(event.startDateTime).format("h a");
    if (event.status === "draft") {
      return `${hour}: available`;
    } else if (event.status === "day_off") {
      return `${hour}: ${event.note}`;
    } else {
      return `${hour}: ${event.client.clientName}`;
    }
  };

  viewChanged = (date: Date) => {
    this.setState({ date });
    this.getAppointmentsAndLeaves(date);
  };

  render() {
    const {
      appointmentsData,
      leavesData,
      showAppointment,
      currentAppointment,
      showLeaveModal,
      selectedLeave,
      showLeaves,
      calendarStartHour,
      calendarEndHour,
    } = this.state;

    const calendarMin = buildCalendarTime(calendarStartHour);
    const calendarMax = buildCalendarTime(calendarEndHour, true);

    const combinedEvents = showLeaves
      ? [...appointmentsData, ...leavesData]
      : appointmentsData;

    return (
      <>
        <div className="container">
          <header className="jumbotron">
            <h3>{this.state.content}</h3>
          </header>
          <h2>{this.state.date.toDateString()}</h2>
          <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
            <Link
              to="/list"
              className="btn btn-outline-secondary btn-sm"
              aria-label="Search"
              title="Search"
            >
              <span aria-hidden="true">🔍</span>
            </Link>
          </div>
          <BigCalendar
            views={["month", "agenda"]}
            selectable
            popup
            localizer={localizer}
            date={this.state.date}
            defaultView="month"
            events={combinedEvents}
            eventPropGetter={eventStyleGetter}
            style={{ height: "100vh" }}
            min={calendarMin}
            max={calendarMax}
            scrollToTime={calendarMin}
            onSelectEvent={this.showModal}
            onNavigate={this.viewChanged}
            startAccessor={this.formatStartDate}
            endAccessor={this.formatEndDate}
            titleAccessor={this.formatTitle}
          />
        </div>
        <Appointment
          show={showAppointment}
          onHide={this.hideModal}
          appointment={currentAppointment}
          onAppointmentChange={this.onAppointmentChanged}
        />
        <LeaveModal
          show={showLeaveModal}
          onHide={() => this.setState({ showLeaveModal: false, selectedLeave: null })}
          leave={selectedLeave}
          onLeaveChanged={this.onAppointmentChanged}
          filterType="dyno"
        />
      </>
    );
  }
}
